import type { Vec3 } from './math.js';
import type { WeaponId } from './weapons.js';

export type PlayerId = number;
export type TeamId = 0 | 1;

/** Input buttons, packed into a uint16 on the wire. Order is part of the protocol. */
export const Button = {
  Forward: 1 << 0,
  Back: 1 << 1,
  Left: 1 << 2,
  Right: 1 << 3,
  Jump: 1 << 4,
  Crouch: 1 << 5,
  Sprint: 1 << 6,
  Fire: 1 << 7,
  Ads: 1 << 8,
  Reload: 1 << 9,
  Interact: 1 << 10,
} as const;

export type ButtonMask = number;

export const hasButton = (mask: ButtonMask, button: number): boolean =>
  (mask & button) !== 0;

/** One tick of player intent. This is the only thing a client is allowed to send. */
export interface InputCommand {
  /** Monotonic per-client sequence number, used for acking and reconciliation. */
  seq: number;
  buttons: ButtonMask;
  yaw: number;
  pitch: number;
  /** Weapon the client believes it has selected (server validates). */
  weapon: number;
}

/** Authoritative per-player state. Mutated in place by the simulation. */
export interface PlayerState {
  id: PlayerId;
  team: TeamId;
  position: Vec3;
  velocity: Vec3;
  yaw: number;
  pitch: number;
  health: number;
  alive: boolean;
  onGround: boolean;
  crouching: boolean;
  sliding: boolean;
  /** Milliseconds remaining on the current slide. */
  slideTimerMs: number;
  slideCooldownMs: number;
  /** Time spent airborne mid-slide — brief drops don't cancel, long falls do. */
  slideAirMs: number;
  coyoteMs: number;
  jumpBufferMs: number;
  wasJumpDown: boolean;
  /** Previous tick's crouch button, for press-edge detection (slide trigger). */
  wasCrouchDown: boolean;
  weapon: WeaponId;
  ammo: number;
  /**
   * Rounds left in each holstered weapon's magazine. Drawn from and stashed to
   * on every switch, so cycling weapons is never a free reload. Cleared on
   * respawn, which is what refills everything for a new round.
   */
  magazines: Partial<Record<WeaponId, number>>;
  reloadEndsAtMs: number;
  nextShotAtMs: number;
  spread: number;
  kills: number;
  deaths: number;
}

export interface HitEvent {
  attacker: PlayerId;
  victim: PlayerId;
  damage: number;
  headshot: boolean;
  killed: boolean;
  point: Vec3;
}

export interface ShotEvent {
  shooter: PlayerId;
  origin: Vec3;
  end: Vec3;
  weapon: WeaponId;
}

export interface SimEvents {
  shots: ShotEvent[];
  hits: HitEvent[];
}

export const emptyEvents = (): SimEvents => ({ shots: [], hits: [] });
